import { Injectable } from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import JSZip, { JSZipObject } from 'jszip';
import { SourceImage } from 'src/app/models/drb-models/source-image';
import { DPI_REGEX, DPI_TYPES } from 'src/app/shared/constants/general-constants';

@Injectable({ providedIn: 'root' })
export class ImageValidation {
  public image: SourceImage;
  public fileName: string;
  public dpiRegex = DPI_REGEX;

  constructor(public toastrService: NbToastrService) {}

  // -----------------------------

  public getDpiName(name: string): string | null {
    const dpiName = name
      .split('/')
      .filter(Boolean)
      .find((dir) => this.dpiRegex.test(dir));
    return dpiName ? dpiName.toLowerCase().replace(/_/g, '-') : null;
  }

  // -----------------------------

  public async validateImage(link: string): Promise<boolean> {
    const dpiTypes = DPI_TYPES;

    try {
      if (link.endsWith('/') || link.endsWith('\\')) {
        throw new Error('trailing slash');
      }

      const imageZip = await this.getZip(link);
      const checkDpis = new Set<string>();

      for (const [name, entry] of Object.entries(imageZip.files)) {
        if (entry.dir) continue;
        if (!/\.(png|jpe?g)$/i.test(name)) continue;
        const dpiName = this.getDpiName(name);

        if (dpiName) checkDpis.add(dpiName.toLowerCase().replace('_', '-'));
      }
      if (checkDpis.size === 0) {
        this.toastrService.show('', $localize`No density folders found`, { status: 'danger' });
        return false;
      } else {
        const missingDpis = dpiTypes.filter((dpis) => !checkDpis.has(dpis));
        if (missingDpis.length) {
          this.toastrService.show('', $localize`Missing densities: ${missingDpis.join(', ')}`, {
            status: 'warning'
          });
        }
      }
      return this.checkFiles(imageZip.files);
    } catch (e) {
      const errorMessage =
        e.message === 'trailing slash' ? $localize`Invalid URL: Remove trailing \\ or /` : $localize`Invalid URL`;

      this.toastrService.show('', errorMessage, { status: 'danger' });
      return false;
    }
  }

  // -----------------------------

  public async getZip(url: string): Promise<JSZip> {
    const response = await fetch(url);
    if (!response.ok) this.toastrService.show('', $localize`Invalid URL`, { status: 'danger' });
    const blob = await response.blob();
    const arrayBuffer = await blob.slice(0, 2).arrayBuffer();
    const byteArray = new Uint8Array(arrayBuffer);

    //checks for pk signature
    if (byteArray[0] !== 0x50 || byteArray[1] !== 0x4b) {
      this.toastrService.show('', $localize`Invalid URL: No .ZIP found`, { status: 'danger' });
    } else {
      const zip = new JSZip();
      return await zip.loadAsync(blob);
    }
  }

  // -----------------------------

  public async checkFiles(files: Record<string, JSZipObject>): Promise<boolean> {
    let allOk = true;
    const namesList = new Set<string>(); //stores unique name (length should be 1)
    let nameOnly: string;

    const imageEntries = Object.entries(files).filter(
      ([name, entry]) =>
        !entry.dir &&
        (name.endsWith('.png') || name.endsWith('.jpg')) &&
        !name.includes('MACOSX') &&
        !name.endsWith('/')
    );
    if (imageEntries.length === 0) {
      this.toastrService.show('', $localize`No PNG/JPG images found`, { status: 'danger' });
      return false;
    }
    for (const [name, entry] of imageEntries) {
      const blob = await entry.async('blob');
      const size = blob.size / 1024;
      const dpiName = this.getDpiName(name);

      // isolates filename
      const dirPath = name.split('/');
      nameOnly = dirPath[dirPath.length - 1];
      namesList.add(nameOnly);

      if (size > 200) {
        this.toastrService.show('', $localize`${dpiName} filesize too large (> 200KB)`, { status: 'danger' });
        allOk = false;
      } else if (namesList.size !== 1) {
        this.toastrService.show('', $localize`Filenames do not match.`, { status: 'danger' });
        allOk = false;
      }
    }
    if (allOk) {
      this.fileName = nameOnly;
      return allOk;
    }
    return false;
  }
}
